import {
    Component, Mixins, Watch,
} from 'vue-property-decorator';
import Classes from '@components/shared/mixins/classes';
import { YIcon, YInput } from '@components/index';
import config from '@components/shared/config';
import { get } from 'lodash/object';
import Props from './props';

/**
 * @name Pagination
 */
@Component({
    components: {
        YIcon,
        YInput,
    },
})
class YPagination extends Mixins(Props, Classes) {
    /**
     * Timeout for change input pagination.
     */
    timeout = null;
    inputPagination : number|string = get(this.$route, 'query.page', null) || 1

    /**
     * Props list that active by developer
     */
    activeCommonProps: Array<string> = ['color', 'size', 'sharp', 'raised', 'outline'];

    /**
     * Props list that available on page number item.
     */
    pageNumberProps: Array<string> = ['sharp', 'raised', 'outline'];

    /**
     * Create Common Props for the pagination.
     * 
     * @returns {object} - List of props
     */
    get commonProps(): Record<string, any> { // eslint-disable-line @typescript-eslint/no-explicit-any
        const commonProps: Record<string, any> = { // eslint-disable-line @typescript-eslint/no-explicit-any
            baseClass: this.baseClass,
        };
        this.activeCommonProps.forEach((item) => {
            commonProps[item] = this[item];
        });
        return commonProps;
    }

    /**
     * Create classes array for the pagination.
     * 
     * @returns {Array} - Array of classes
     */
    get classes(): Array<string> {
        let cls: Array<string> = [];

        cls.push(...this.commonClasses('Pagination', this.commonProps, this.baseClass || config.prefix.pagination));
        if (this.disabled) {
            cls.push('is-disabled');
        }

        // Remove item prop from root class
        cls = cls.filter((item) => !this.pageNumberProps.includes(item.replace('is-', '')));

        return cls;
    }

    /**
     * Create classes array for the page number item.
     * 
     * @returns {Array} - Array of classes
     */
    get itemClasses(): Array<string> {
        const cls: Array<string> = [];

        if (this.raised) {
            cls.push('is-raised');
        }
        if (this.sharp) {
            cls.push('is-sharp');
        }
        if (this.circle) {
            cls.push('is-circle');
        }
        if (this.outline) {
            cls.push('is-outline');
        }
        if (this.text) {
            cls.push('is-text');
        }

        return cls;
    }

    /**
     * Return the class of the pagination from config.
     * 
     * @returns {string}
     */
    get paginationClass(): string {
        return config.prefix.pagination;
    }

    /**
     * Gets a list of pagination items.
     *
     * @returns {*}
     */
    get paginator(): unknown {
        return this.setPaginator(this.current, Number(this.lastPage));
    }

    /**
     * Checks if link is using params for pagination.
     *
     * @returns {boolean}
     */
    get useParams(): boolean {
        return !!(this.$route.params && this.$route.params?.page);
    }

    /**
     * Gets current page number.
     *
     * @returns {number}
     */
    get current(): number {
        if (this.useParams) {
            return this.$route.params.page ? parseInt(this.$route.params.page, 10) : 1;
        }

        if (this.$route.query?.page) {
            return parseInt(this.$route.query.page.toString(), 10);
        }

        return 1;
    }

    /**
     * Check Is First Page.
     * 
     * @returns {boolean}
     */
    get isFirstPage(): boolean {
        return this.current === 1;
    }

    /**
     * Check Is Last Page.
     * 
     * @returns {boolean}
     */
    get isLastPage(): boolean {
        return this.current === parseInt(this.lastPage.toString(), 10);
    }

    /**
     * Decides whether pagination needs "..." or not.
     *
     * @param {number} current - Current page number.
     * @param {number} last - last page number.
     * @returns {Array}
     */
    setPaginator(current: number, last: number): Array<string | unknown> {
        if (last <= 10) {
            return this.setRange(last);
        }
        return this.setRangeWithDots(current, last, 3);
    }

    /**
     * Gets a list of pagination numbers.
     *
     * @param {number} last - last page number
     * @returns {Array}
     */
    setRange(last: number): Array<string | unknown> {
        const range: Array<number> = [];

        for (let i = 1; i <= last; i += 1) {
            range.push(i);
        }

        return range;
    }

    /**
     * Gets a list of pagination numbers and "...".
     *
     * @param {number} current - current page number
     * @param {number} last - last page number
     * @param {number} margin - number space between two dots in list
     * @returns {Array}
     */
    setRangeWithDots(current: number, last: number, margin: number): Array<string | number> {
        const rangeWithDots: Array<string | number> = [];

        if (current >= margin) {
            rangeWithDots.push(1);
            rangeWithDots.push('...');
        }

        if (current < margin) {
            for (let i = 1; i <= margin; i += 1) {
                if (i <= margin) {
                    rangeWithDots.push(i);
                }
            }
        } else if (current > last - margin + 1) {
            rangeWithDots.push(last - 2);
            rangeWithDots.push(last - 1);
            rangeWithDots.push(last);
        } else {
            rangeWithDots.push(current - 1);
            rangeWithDots.push(current);
            rangeWithDots.push(current + 1);
        }

        if (last - current >= margin) {
            rangeWithDots.push('...');
        }

        if (current < last - 1) {
            rangeWithDots.push(last);
        }

        return rangeWithDots;
    }

    /**
     * Computes route object.
     *
     * @param {number} page - The page number
     * @returns {{query: {page: *}}|{params: {page: *}}}
     */
    routeComputer(page : number): unknown {
        if (this.useParams) {
            return {
                ...this.$route,
                params: {
                    ...this.$route.params,
                    page,
                },
            };
        }

        return {
            ...this.$route,
            query: {
                ...this.$route.query,
                page,
            },
        };
    }

    /**
     * Change route on update input
     */
    @Watch('inputPagination', { immediate: true, deep: true })
    onPageChanged() {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
            if (this.$route.query.page !== this.inputPagination && this.inputPagination !== 1) {
                this.$router.replace({
                    ...this.$route,
                    query: {
                        ...this.$route.query,
                        page: this.inputPagination.toString(),
                    },
                }).catch((error) => {
                    console.error(error);
                });
            }
        }, 1000);
    }

    /**
     * Change pagination on update route
     *
     * @param {number|string} val
     */
    @Watch('$route.query.page', { immediate: true, deep: true })
    onRouteChanged(val: number|string) {
        if (val) {
            this.inputPagination = val;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    numbers: Record<string, any> = {
        fa: ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'],
        ar: ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'],
        en: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
    }

    /**
     * Converts English digits into asked locale formatted number.
     *
     * @param {number} digit - The number to convert
     * @param {number} locale - The locale to convert
     * @returns {string}
     */
    digits = (digit: number, locale = this.lang): string => `${digit}`
        .replace(/[0123456789]/g, (w) => this.numbers[locale][this.numbers.en.indexOf(w)])
        .replace(/[۰۱۲۳۴۵۶۷۸۹]/g, (w) => this.numbers[locale][this.numbers.fa.indexOf(w)])
        .replace(/[٠١٢٣٤٥٦٧٨٩]/g, (w) => this.numbers[locale][this.numbers.ar.indexOf(w)])
}

export default YPagination;
